// Generic document generation (fully client-side, no backend/LLM).
//
// This takes whatever the user asked for (a free-text prompt + the current
// document) and returns an updated compliance document as markdown. All edits
// are produced locally with lightweight heuristics so the studio is a
// self-contained, always-working demo.

import mammoth from "mammoth";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Derive a reasonable title from a free-form request.
export const deriveTitle = (prompt = "") => {
  const cleaned = prompt.replace(/\s+/g, " ").trim();
  if (!cleaned) return "Untitled document";

  // Pull the noun phrase after common lead-in verbs.
  const match = cleaned.match(
    /(?:draft|create|generate|build|write|prepare|make)(?:\s+(?:an?|the))?\s+(.+?)(?:\s+(?:for|aligned|based|that|to|covering|about|which|using)\b|[.!?]|$)/i
  );
  const candidate = (match ? match[1] : cleaned).trim();
  const titled = candidate
    .split(" ")
    .slice(0, 8)
    .map((w) => (w.length > 3 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
  return titled.charAt(0).toUpperCase() + titled.slice(1);
};

const today = () =>
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

// Build a generic, professional compliance document scaffold from any prompt.
const buildDraft = (prompt, title) => {
  return `# ${title}

> **Status:** Draft &nbsp;·&nbsp; **Generated:** ${today()} &nbsp;·&nbsp; **Owner:** _[assign owner]_

## 1. Purpose & Scope
This document was generated in response to the request:

> _"${prompt.trim()}"_

It establishes the objectives, scope, and boundaries relevant to the requested
subject matter. Replace the bracketed placeholders with organization-specific
details before publishing.

## 2. Roles & Responsibilities
| Role | Responsibility |
| --- | --- |
| Document Owner | Maintains and approves this document |
| Compliance Team | Reviews for regulatory alignment |
| All Staff | Adhere to the requirements described herein |

## 3. Policy / Requirement Statements
1. The organization shall define and enforce the controls relevant to this document.
2. Responsibilities shall be clearly assigned and communicated.
3. Records shall be retained in accordance with applicable retention schedules.
4. This document shall be reviewed at least annually or upon significant change.

## 4. Controls & Safeguards
- **Access Control** — Least-privilege and role-based access are enforced.
- **Monitoring** — Relevant activity is logged and reviewed.
- **Data Protection** — Sensitive data is encrypted in transit and at rest.
- **Incident Handling** — Deviations are reported and remediated promptly.

## 5. Compliance Mapping
| Framework | Reference |
| --- | --- |
| SOC 2 | CC-series criteria |
| ISO 27001 | Annex A controls |
| GDPR | Applicable articles |

## 6. Review & Approval
| Version | Date | Author | Approver |
| --- | --- | --- | --- |
| 0.1 | ${today()} | Rego AI | _[pending]_ |

---
_This draft was generated by Rego AI. Review and tailor all sections before adoption._
`;
};

// Build a starter template for a file that has no content yet. The studio is
// always scoped to a file, so when a fresh one is opened we seed it with this.
export const buildTemplate = ({ title, docType = "document" } = {}) => {
  const heading = title || "Untitled document";
  const kind = docType.charAt(0).toUpperCase() + docType.slice(1);
  return `# ${heading}

> **Type:** ${kind} &nbsp;·&nbsp; **Status:** Template &nbsp;·&nbsp; **Owner:** _[assign owner]_

## 1. Purpose
_Describe why this ${docType} exists and the outcome it drives._

## 2. Scope
_Define who and what this ${docType} applies to (systems, teams, data, locations)._

## 3. Roles & Responsibilities
| Role | Responsibility |
| --- | --- |
| Owner | _[responsibility]_ |
| Reviewer | _[responsibility]_ |
| All Staff | _[responsibility]_ |

## 4. Requirements
1. _[requirement]_
2. _[requirement]_
3. _[requirement]_

## 5. Compliance Mapping
| Framework | Reference |
| --- | --- |
| SOC 2 | _[criteria]_ |
| ISO 27001 | _[Annex A control]_ |

## 6. Review & Approval
| Version | Date | Author | Approver |
| --- | --- | --- | --- |
| 0.1 | ${today()} | _[author]_ | _[pending]_ |

---
_Ask Rego AI to fill any section \u2014 e.g. "draft the purpose and scope" or "add access-control requirements aligned to ISO 27001."_
`;
};

// A short conversational reply to accompany the generated draft.
const buildAssistantReply = (title) =>
  `I've put together a first draft of **${title}** on the right. ` +
  `It's structured with purpose, roles, requirement statements, controls, and a ` +
  `compliance mapping. Tell me what to refine \u2014 tighten the scope, add a ` +
  `framework, change the tone, or expand any section.`;

/* --------------------- Local (frontend-only) "AI" editor ------------------ */

// A small library of well-written compliance sections. When the user's request
// matches one of these topics, we insert/refresh that section in the document
// so the edit looks meaningful in a demo — no backend required.
const SECTION_LIBRARY = [
  {
    keys: ["retention", "retain", "archiv"],
    title: "Data Retention",
    body: `Records covered by this document are retained only as long as required for the stated business, legal, and regulatory purposes.

- **Active data** is retained for the duration of the applicable business need.
- **Archived data** is retained per the organization's retention schedule and applicable regulations.
- **Disposal** is performed securely once the retention period lapses, with disposal events logged for audit.`,
  },
  {
    keys: ["access control", "least privilege", "rbac", "permission", "access"],
    title: "Access Control",
    body: `Access to systems and data is granted on a least-privilege, need-to-know basis.

- Access is provisioned through role-based access control (RBAC) and approved by the resource owner.
- Privileged access is restricted, logged, and reviewed at least quarterly.
- Access is revoked promptly upon role change or termination.`,
  },
  {
    keys: ["incident", "breach", "response"],
    title: "Incident Response",
    body: `Security events are detected, triaged, and remediated through a defined incident-response process.

1. **Detect & report** — anyone who observes a suspected incident reports it immediately.
2. **Assess & contain** — the response team classifies severity and contains impact.
3. **Eradicate & recover** — root cause is removed and affected services restored.
4. **Review** — a post-incident review captures lessons learned and corrective actions.`,
  },
  {
    keys: ["digital signature", "signing key", "sign document", "sign the document"],
    title: "Digital Signature Handling",
    body: `Digital signatures shall be used to ensure the authenticity and integrity of electronic information where required. Approved cryptographic algorithms and trusted digital certificates shall be used for signing activities. Private signing keys must be securely protected and shall not be shared, hardcoded, or stored in unsecured locations. Any suspected compromise of a signing key shall be reported immediately, and the affected key shall be revoked and replaced in accordance with the Incident Response Policy.`,
  },
  {
    keys: ["encrypt", "cryptograph", "key management"],
    title: "Encryption & Key Management",
    body: `Sensitive information is protected using industry-standard cryptography.

- Data is encrypted **in transit** (TLS 1.2+) and **at rest** (AES-256 or equivalent).
- Cryptographic keys are stored in a managed key store with restricted access.
- Keys are rotated on a defined schedule and revoked immediately on suspected compromise.`,
  },
  {
    keys: ["training", "awareness"],
    title: "Security Awareness & Training",
    body: `All personnel receive security awareness training appropriate to their role.

- Training is completed at onboarding and refreshed at least annually.
- Role-specific training is provided for staff with elevated access.
- Completion is tracked and reported to management.`,
  },
  {
    keys: ["vendor", "third party", "third-party", "supplier"],
    title: "Third-Party & Vendor Management",
    body: `Third parties that handle organizational data are assessed and monitored.

- Vendors undergo security due diligence before onboarding.
- Contracts include appropriate data-protection and security obligations.
- Vendor risk is reviewed periodically and upon material change.`,
  },
  {
    keys: ["backup", "recovery", "continuity", "disaster"],
    title: "Backup & Recovery",
    body: `Critical data and systems are backed up and recoverable within defined objectives.

- Backups run on a defined schedule and are stored securely, separated from production.
- Restores are tested periodically to validate integrity and recovery objectives (RPO/RTO).
- Backup access is restricted and logged.`,
  },
];

const REVISION_MARKER = "<!-- rego-ai-revisions -->";

// Insert new markdown content just before a trailing "---" footer, if present,
// otherwise append it to the end of the document.
const insertBeforeFooter = (doc, block) => {
  const footerIdx = doc.lastIndexOf("\n---");
  if (footerIdx === -1) return `${doc.trimEnd()}\n\n${block}\n`;
  return `${doc.slice(0, footerIdx).trimEnd()}\n\n${block}\n${doc.slice(footerIdx)}`;
};

// Next "## N." section number based on what already exists in the doc.
const nextSectionNumber = (doc) => {
  const nums = [...doc.matchAll(/^##\s+(\d+)\./gm)].map((m) => Number(m[1]));
  return nums.length ? Math.max(...nums) + 1 : null;
};

// Replace an existing "## ... Title" section body, or return null if absent.
const replaceSection = (doc, title, newBody) => {
  const re = new RegExp(
    `(^##\\s+(?:\\d+\\.\\s+)?${title}[^\\n]*\\n)([\\s\\S]*?)(?=\\n##\\s|\\n---|$)`,
    "m"
  );
  if (!re.test(doc)) return null;
  return doc.replace(re, `$1\n${newBody}\n`);
};

// Revision notes are intentionally omitted — they insert at the top of the doc
// which breaks diff-streaming (common prefix shrinks to ~2 chars and the whole
// doc retypes).  For demo purposes a clean document is better.
const addRevisionNote = (doc) => doc;

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Document Information fields users commonly fill in via chat.
const METADATA_FIELDS = [
  {
    id: "companyName",
    label: "Company Name",
    triggerKeys: ["company name", "organization name", "organisation name", "company"],
    apply: (doc, value) => {
      let d = doc;
      // "[Insert Company Name]", "[Company Name]", "[ Company Name ]", etc.
      d = d.replace(/\[\s*(?:Insert\s+)?(?:Your\s+)?Company\s*(?:Name|'s\s+Name)?\s*\]/gi, value);
      // "**Company Name**: [Insert …]" style table rows — catch any remaining bracket after the label
      d = d.replace(/(Company\s*(?:Name)?[:\s]+)\[[^\]]{0,60}\]/gi, `$1${value}`);
      return d;
    },
  },
  {
    id: "documentAuthor",
    label: "Document Author",
    triggerKeys: ["document author", "doc author", "author name", "author"],
    apply: (doc, value) => {
      let d = doc;
      d = d.replace(/\[\s*(?:Insert\s+)?(?:Your\s+)?(?:Document\s+)?Author(?:\s*Name)?\s*\]/gi, value);
      d = d.replace(/((?:Document\s+)?Author[:\s]+)\[[^\]]{0,60}\]/gi, `$1${value}`);
      return d;
    },
  },
  {
    id: "reviewer",
    label: "Reviewer",
    triggerKeys: ["reviewer", "document reviewer"],
    apply: (doc, value) => {
      let d = doc;
      d = d.replace(/\[\s*(?:Insert\s+)?(?:Your\s+)?(?:Document\s+)?Reviewer(?:\s*Name)?\s*\]/gi, value);
      d = d.replace(/(Reviewer[:\s]+)\[[^\]]{0,60}\]/gi, `$1${value}`);
      return d;
    },
  },
];

const extractMetadataValue = (prompt, triggerKeys) => {
  const segments = [
    prompt.trim(),
    ...prompt.split(/\s+and\s+/i).map((s) => s.trim()).filter(Boolean),
  ];

  const keys = [...triggerKeys].sort((a, b) => b.length - a.length);

  for (const segment of segments) {
    for (const key of keys) {
      const k = escapeRegex(key);
      const patterns = [
        new RegExp(
          `(?:change|set|update|make|put)\\s+(?:the\\s+)?${k}\\s+(?:to|as|is)\\s+["']?(.+?)["']?$`,
          "i"
        ),
        new RegExp(`^${k}\\s+(?:to|as|is)\\s+["']?(.+?)["']?$`, "i"),
        new RegExp(`${k}\\s*[:=]\\s+["']?(.+?)["']?$`, "i"),
      ];
      for (const re of patterns) {
        const m = segment.match(re);
        if (m?.[1]?.trim()) return m[1].trim();
      }
    }
  }
  return null;
};

const applyMetadataUpdates = (doc, prompt) => {
  const updates = METADATA_FIELDS.map((field) => ({
    field,
    value: extractMetadataValue(prompt, field.triggerKeys),
  })).filter((u) => u.value);

  if (!updates.length) return null;

  let updated = doc;
  for (const { field, value } of updates) {
    updated = field.apply(updated, value);
  }

  const labels = updates.map((u) => u.field.label).join(", ");
  const summary = updates
    .map((u) => `**${u.field.label}** → "${u.value}"`)
    .join(", ");

  return {
    document: addRevisionNote(updated, `Updated ${labels}.`),
    reply: `Done — I updated ${summary} in the Document Information section. Anything else to change?`,
  };
};

export const getMetadataFieldLabel = (fieldId) =>
  METADATA_FIELDS.find((f) => f.id === fieldId)?.label || "field";

export const getMetadataAskMessage = (fieldId) => {
  const label = getMetadataFieldLabel(fieldId);
  return `What should the **${label}** be? Just type the name below.`;
};

export const applyMetadataField = (doc, fieldId, value) => {
  const field = METADATA_FIELDS.find((f) => f.id === fieldId);
  const trimmed = (value ?? "").trim();
  if (!field || !trimmed) return null;

  const updated = field.apply(doc || "", trimmed);

  // If the regex made no change the placeholder may be in an unexpected format.
  // Append a prominent notice so it's visible in the preview without silently no-oping.
  const changed = updated !== (doc || "");
  const docWithNote = changed
    ? addRevisionNote(updated, `Updated ${field.label}.`)
    : addRevisionNote(
        `${updated}\n\n**${field.label}**: ${trimmed}`,
        `Set ${field.label} to "${trimmed}".`
      );

  return {
    document: docWithNote,
    reply: changed
      ? `Done — I set **${field.label}** to "${trimmed}". Anything else to change?`
      : `I set **${field.label}** to "${trimmed}". (The document's placeholder wasn't found — I've appended it at the end instead.) Want me to adjust anything else?`,
  };
};

// Produce an edited document + a human reply, purely from the prompt heuristics.
const applyLocalEdit = (currentDoc, prompt) => {
  const doc = currentDoc || "";
  const lower = prompt.toLowerCase();

  // 0. Document Information metadata (company name, author, reviewer, …)
  const metadataEdit = applyMetadataUpdates(doc, prompt);
  if (metadataEdit) return metadataEdit;

  // 1. "Tighten / shorten / clean up" — collapse noise, keep structure.
  if (/(tighten|shorten|concise|trim|redundan|clean up|clean-up|polish)/.test(lower)) {
    const tightened = doc
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trimEnd();
    return {
      document: addRevisionNote(`${tightened}\n`, "Tightened wording and removed redundant spacing."),
      reply:
        "I tightened the language and cleaned up the formatting while keeping every section intact. Want me to condense a specific section further?",
    };
  }

  // 2. Topic match — insert or refresh a known compliance section.
  const match = SECTION_LIBRARY.find((s) => s.keys.some((k) => lower.includes(k)));
  if (match) {
    const replaced = replaceSection(doc, match.title, match.body);
    if (replaced) {
      return {
        document: addRevisionNote(replaced, `Updated the “${match.title}” section.`),
        reply: `I refreshed the **${match.title}** section with clearer, audit-ready language. Anything else to adjust?`,
      };
    }
    const n = nextSectionNumber(doc);
    const heading = n ? `## ${n}. ${match.title}` : `## ${match.title}`;
    const withSection = insertBeforeFooter(doc, `${heading}\n\n${match.body}`);
    return {
      document: addRevisionNote(withSection, `Added a “${match.title}” section.`),
      reply: `I added a **${match.title}** section to the document. Take a look on the right and tell me what to refine.`,
    };
  }

  // 3. Generic request — add a professionally-worded section echoing the ask.
  const genericTitle = deriveTitle(prompt);
  const n = nextSectionNumber(doc);
  const heading = n ? `## ${n}. ${genericTitle}` : `## ${genericTitle}`;
  const body = `This section was added in response to the request: _"${prompt.trim()}"_.

- The organization shall define and maintain the controls relevant to this requirement.
- Responsibilities shall be clearly assigned and communicated to affected staff.
- Compliance shall be reviewed at least annually or upon significant change.`;
  const withSection = insertBeforeFooter(doc, `${heading}\n\n${body}`);
  return {
    document: addRevisionNote(withSection, `Added a “${genericTitle}” section.`),
    reply: `I drafted a **${genericTitle}** section based on your request and added it to the document. Refine it further any time.`,
  };
};

/**
 * Stream `toDoc` progressively via `onChunk`.
 *
 * For refinements, we find the common prefix with `fromDoc` and instantly
 * show the unchanged portion, then stream only the new/changed tail so the
 * user sees just the fresh content being written — not the full doc again.
 *
 * @param {string}   fromDoc       Current document (before edit). Pass "" for new docs.
 * @param {string}   toDoc         Target document to stream toward.
 * @param {Function} onChunk       Called with progressively longer slices of toDoc.
 * @param {number}   [tokensPerStep=4]
 * @param {number}   [delayMs=30]
 */
export const streamDocument = async (fromDoc, toDoc, onChunk, tokensPerStep = 4, delayMs = 30) => {
  // Find where the two documents first diverge.
  let commonLen = 0;
  const minLen = Math.min((fromDoc || "").length, toDoc.length);
  while (commonLen < minLen && fromDoc[commonLen] === toDoc[commonLen]) {
    commonLen++;
  }

  // Retreat to the nearest newline so we don't split mid-word.
  const splitAt = Math.max(0, toDoc.lastIndexOf("\n", commonLen));
  const prefix = toDoc.slice(0, splitAt);
  const suffix = toDoc.slice(splitAt);

  // Show everything up to the change point instantly.
  if (prefix) onChunk(prefix);

  // Then type the rest in slowly.
  const tokens = suffix.split(/(\s+)/);
  let acc = prefix;
  for (let i = 0; i < tokens.length; i += tokensPerStep) {
    acc += tokens.slice(i, i + tokensPerStep).join("");
    onChunk(acc);
    // eslint-disable-next-line no-await-in-loop
    await wait(delayMs);
  }
};

/**
 * Generate (or refine) a document — fully client-side.
 *
 * @param {Object}   params
 * @param {string}   params.prompt        Latest user message.
 * @param {string}   [params.currentDoc]  Existing draft, if refining.
 * @param {Function} [params.onChunk]     Optional streaming callback(partialMarkdown).
 * @returns {Promise<{ title: string, document: string, reply: string }>}
 */
export const requestGeneration = async ({ prompt, currentDoc = "", onChunk } = {}) => {
  const isRefinement = Boolean(currentDoc);

  let document;
  let reply;
  let title;

  if (isRefinement) {
    const edited = applyLocalEdit(currentDoc, prompt);
    document = edited.document;
    reply = edited.reply;
    title = document.match(/^#\s+(.+)$/m)?.[1] || deriveTitle(prompt);
  } else {
    title = deriveTitle(prompt);
    document = buildDraft(prompt, title);
    reply = buildAssistantReply(title);
  }

  if (typeof onChunk === "function") {
    // Refinements: only stream from where the content changed (don't retype the whole doc).
    // New docs: stream everything from scratch.
    // 2 tokens / 40 ms ≈ one word every ~80 ms — clearly visible typing speed.
    await streamDocument(isRefinement ? currentDoc : "", document, onChunk, 2, 40);
  } else {
    await wait(600);
  }

  return { title, document, reply };
};

/* ---------------------- Load a real document from source ------------------ */

// Mammoth often emits Word bookmark anchors and other HTML in "markdown" output.
// Strip those so ReactMarkdown renders clean policy text.
export const cleanImportedMarkdown = (markdown = "") => {
  let text = markdown;

  // Empty Word bookmark anchors: <a id="_abc"></a>
  text = text.replace(/<a\b[^>]*>\s*<\/a>/gi, "");
  text = text.replace(/<a\b[^>]*\/>/gi, "");

  // Other empty Word/HTML noise
  text = text.replace(/<o:p>\s*<\/o:p>/gi, "");
  text = text.replace(/<span\b[^>]*>\s*<\/span>/gi, "");

  // Line breaks & unwrap links (keep link text)
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, "$1");

  // Drop any remaining HTML tags but keep inner text
  text = text.replace(/<[^>]+>/g, "");

  // Common entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));

  // Mammoth escapes "[" as "\[" to prevent markdown link interpretation.
  // Unescape so placeholder regexes like /\[Insert Company Name\]/ can match.
  text = text.replace(/\\\[/g, "[").replace(/\\\]/g, "]");

  // Word callout boxes become markdown blockquotes ("> ...") — flatten to normal text
  text = text.replace(/^>\s?/gm, "");

  // Tidy whitespace
  text = text
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text;
};

const rawTextToMarkdown = (text = "") => {
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const firstIdx = lines.findIndex((l) => l.length > 0);
  if (firstIdx === -1) return "";

  const title = lines[firstIdx];
  const out = [`# ${title}`, ""];

  for (let i = firstIdx + 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line) {
      if (out[out.length - 1] !== "") out.push("");
      continue;
    }
    if (/^\d+\.\s+[A-Za-z]/.test(line) || /^document information$/i.test(line)) {
      out.push(`## ${line}`, "");
    } else if (line.startsWith("•")) {
      out.push(`- ${line.slice(1).trim()}`);
    } else {
      out.push(line, "");
    }
  }

  return cleanImportedMarkdown(out.join("\n"));
};

const toAbsoluteUrl = (sourceUrl) => {
  if (/^https?:\/\//i.test(sourceUrl)) return sourceUrl;
  const base = (process.env.REACT_APP_CLOUDFRONT_URL || "").replace(/\/+$/, "");
  return `${base}/${String(sourceUrl).replace(/^\/+/, "")}`;
};

/**
 * Fetch a source document (currently .docx) and convert it to Markdown so it
 * can be loaded into the studio and edited. Returns "" if unsupported/failed,
 * letting the caller fall back to a template.
 *
 * @param {string} sourceUrl  Relative CloudFront path or absolute URL.
 * @returns {Promise<string>} Markdown, or "" on failure.
 */
export const loadDocumentFromSource = async (sourceUrl) => {
  if (!sourceUrl) return "";

  const candidates = [sourceUrl];
  // Policies are often stored as .docx with a .pdf preview — try both.
  if (/\.pdf(\?|$)/i.test(sourceUrl)) {
    candidates.unshift(sourceUrl.replace(/\.pdf(\?.*)?$/i, ".docx$1"));
  }

  for (const candidate of candidates) {
    if (!/\.docx?(\?|$)/i.test(candidate)) continue;

    try {
      const res = await fetch(toAbsoluteUrl(candidate));
      if (!res.ok) continue;
      const arrayBuffer = await res.arrayBuffer();

      if (typeof mammoth.convertToMarkdown === "function") {
        const { value } = await mammoth.convertToMarkdown({ arrayBuffer });
        const cleaned = cleanImportedMarkdown(value);
        if (cleaned) return cleaned;
      }
      const { value } = await mammoth.extractRawText({ arrayBuffer });
      const markdown = rawTextToMarkdown(value);
      if (markdown.trim()) return markdown;
    } catch (e) {
      console.error("Failed to load source document:", candidate, e);
    }
  }

  return "";
};
